import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Tooltip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBackRounded,
  SaveRounded,
  School as SchoolIcon,
  RocketLaunchRounded,
  StairsRounded,
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { useAcademic } from '../../hooks/use-academic';
import { ManageDocumentRequirements } from './components/ManageDocumentRequirements';
import { ManageReviewCommittee } from './components/ManageReviewCommittee';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';
import { detailEditable, programTabs } from './academic-structure.actions';
import {
  academicApi,
  type ProgramReviewerConfig,
  type ProgramFeeConfig,
  type ProgramCurriculumVersionDto,
  type CourseEquivalenceDto,
} from '../../lib/api/academic.api';
import { terminoParaGuardar } from '../../lib/curriculum-term';
import { RepeatableListEditor, type RepeatableListItem } from './components/RepeatableListEditor';
import { RubricCatalogEditor, type Rubric } from './components/RubricEditor';
import { CurriculumMapEditor, type CourseOption } from './components/CurriculumMapEditor';
import { CourseEquivalenceEditor } from './components/CourseEquivalenceEditor';
import { MallaView, type MallaCourseMeta } from './components/MallaView';
import type { AcademicLevel, CapCode, SubjectAreaCode } from '@dosfilos/domain';
import { buildProgramLadder, stackChain, type LadderProgram } from '@dosfilos/domain';
import { errorText } from '../../lib/error-text';
import { toLadderProgram } from '../../lib/program-ladder';

// ─── Constants ────────────────────────────────────────────────────────────────

// Curated palette mirroring the templates-dashboard fallback colors. Picking
// a swatch sets the program's `primaryColor`; leaving it blank lets the
// dashboard auto-assign from the same palette by program-id hash.
const PROGRAM_COLOR_SWATCHES = [
  '#7C3AED', // purple
  '#0EA5E9', // sky
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#F97316', // orange
];

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 1.2,
          mb: 2,
          display: 'block',
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

// ─── Tab panel ────────────────────────────────────────────────────────────────
// Panels stay MOUNTED (hidden via display:none) so the single form submit still
// includes fields in inactive tabs — the page is one <form> with one Save.

function TabPanel({
  value,
  index,
  children,
}: {
  value: number;
  index: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface FormTemplateOption {
  id: string;
  name: string;
  fieldsCount: number;
}

/** Los aranceles mientras se editan: la moneda puede estar sin elegir. */
type FeeDraft = Omit<ProgramFeeConfig, 'currency'> & {
  currency: ProgramFeeConfig['currency'] | '';
};
const EMPTY_FEE: FeeDraft = {
  enrollmentFee: 0,
  programTotalFee: 0,
  currency: '',
  pricePerCredit: 0,
  pricingMode: 'fixed',
};

export function ProgramDetailPage() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const { currentTenantId } = useTenantStore();
  const { updateProgram, isUpdatingProgram } = useAcademic();

  // Form state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [durationTerms, setDurationTerms] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'archived'>('active');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [documentRequirements, setDocumentRequirements] = useState<any[]>([]);
  const [reviewerConfig, setReviewerConfig] = useState<ProgramReviewerConfig[]>([]);
  // Aranceles: SIN moneda por defecto (la elige quien configura), y no se
  // guardan si nadie los configuró — guardar el programa no puede inventarle
  // aranceles en 0. «Sin arancel» es una marca explícita (H4).
  const [feeConfig, setFeeConfig] = useState<FeeDraft>(EMPTY_FEE);
  const [feeConfigured, setFeeConfigured] = useState(false);
  const [noTuition, setNoTuition] = useState(false);
  const updateFee = (fn: (p: FeeDraft) => FeeDraft) => {
    setFeeConfigured(true);
    setFeeConfig(fn);
  };
  const [primaryColor, setPrimaryColor] = useState<string>('');
  const [minAttendancePct, setMinAttendancePct] = useState<number | ''>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [learningObjectives, setLearningObjectives] = useState<RepeatableListItem[]>([]);
  const [philosophy, setPhilosophy] = useState<RepeatableListItem[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [curriculumVersions, setCurriculumVersions] = useState<ProgramCurriculumVersionDto[]>([]);
  const [courseEquivalences, setCourseEquivalences] = useState<CourseEquivalenceDto[]>([]);
  const [currentCatalogYear, setCurrentCatalogYear] = useState<string | null>(null);
  const [enforcePrerequisites, setEnforcePrerequisites] = useState(false);
  const [minCoursesPerTerm, setMinCoursesPerTerm] = useState<number | ''>('');
  const [offeringMode, setOfferingMode] = useState<'open_catalog' | 'curated_cohort'>(
    'curated_cohort',
  );
  // TMAI CAP the program targets (1:1). Empty code = no target.
  const [capTargetCode, setCapTargetCode] = useState<'' | CapCode>('');
  const [capTargetLevel, setCapTargetLevel] = useState<AcademicLevel>('undergraduate');
  // The program this one builds on in a ladder of stacked programs ('' = none),
  // and every program, to offer the choice and to say where this one starts.
  const [stacksOnProgramId, setStacksOnProgramId] = useState<string>('');
  const [enforceStackEntry, setEnforceStackEntry] = useState(false);
  const [includesStackedPrograms, setIncludesStackedPrograms] = useState(false);
  // The stored rules, so Save only calls the (role-gated) callable when one of
  // them actually changed — faculty can open this screen too.
  const [savedLadderRules, setSavedLadderRules] = useState({
    stacksOnProgramId: '',
    enforceStackEntry: false,
    includesStackedPrograms: false,
  });
  const [allPrograms, setAllPrograms] = useState<LadderProgram[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  // Enrichment for the visual malla: prerequisites (course doc) + CAP area/TCU
  // (from the course's CCD on its courseTemplate).
  const [coursePrereqs, setCoursePrereqs] = useState<Record<string, string[]>>({});
  const [courseCap, setCourseCap] = useState<
    Record<string, { area?: SubjectAreaCode; tcu?: number }>
  >({});

  // UI state
  const [tab, setTab] = useState(0);
  const canManage = useCanManageAcademics();
  // Quien no gestiona CONSULTA el programa: sin Guardar, sin Finanzas, campos
  // bloqueados (2a-1b). El servidor ya rechazaba guardar (updateProgram exige
  // SECTION_MANAGEMENT_ROLES); un botón que siempre falla parece un error.
  const editable = detailEditable({ canManage });
  const tabs = programTabs({ canManage });
  const [curriculumView, setCurriculumView] = useState<'malla' | 'edit'>('malla');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [programName, setProgramName] = useState('');
  const [templates, setTemplates] = useState<FormTemplateOption[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string; code: string }[]>([]);

  // Load program via realtime snapshot
  useEffect(() => {
    if (!currentTenantId || !programId) return;
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, `tenants/${currentTenantId}/programs/${programId}`),
      (snap) => {
        setLoading(false);
        if (!snap.exists()) {
          setError(t('programDetail.notFound'));
          return;
        }
        const d = snap.data();
        setProgramName(d['name'] || '');
        setName(d['name'] || '');
        setCode(d['code'] || '');
        setDescription(d['description'] || '');
        setPurpose(d['purpose'] || '');
        setDurationTerms(d['durationTerms'] || '');
        setStatus(d['status'] || 'active');
        setFormTemplateId(d['formTemplateId'] || '');
        setDocumentRequirements(d['documentRequirements'] || []);
        setReviewerConfig(d['reviewerConfig'] || []);
        setFeeConfig(d['feeConfig'] ?? EMPTY_FEE);
        setFeeConfigured(!!d['feeConfig']);
        setNoTuition(d['noTuition'] === true);
        setPrimaryColor((d['primaryColor'] as string | undefined) ?? '');
        setMinAttendancePct(typeof d['minAttendancePct'] === 'number' ? d['minAttendancePct'] : '');
        const storedLadder = {
          stacksOnProgramId: (d['stacksOnProgramId'] as string | undefined) ?? '',
          enforceStackEntry: d['enforceStackEntry'] === true,
          includesStackedPrograms: d['includesStackedPrograms'] === true,
        };
        setStacksOnProgramId(storedLadder.stacksOnProgramId);
        setEnforceStackEntry(storedLadder.enforceStackEntry);
        setIncludesStackedPrograms(storedLadder.includesStackedPrograms);
        setSavedLadderRules(storedLadder);
        setDepartmentId((d['departmentId'] as string | undefined) ?? '');
        setLearningObjectives(
          (
            (d['learningObjectives'] as
              | { id: string; code?: string; text: string }[]
              | undefined) ?? []
          ).map((o) => ({
            id: o.id,
            ...(o.code ? { code: o.code } : {}),
            text: o.text,
          })),
        );
        setPhilosophy(
          (
            (d['philosophy'] as { id: string; title: string; description: string }[] | undefined) ??
            []
          ).map((p) => ({
            id: p.id,
            code: p.title,
            text: p.description,
          })),
        );
        const rubricsRaw = (d['rubrics'] as Rubric[] | undefined) ?? null;
        if (rubricsRaw && Array.isArray(rubricsRaw) && rubricsRaw.length > 0) {
          setRubrics(rubricsRaw);
        } else if (d['researchRubric']) {
          setRubrics([d['researchRubric'] as Rubric]);
        } else {
          setRubrics([]);
        }
        setCurriculumVersions(
          (d['curriculumVersions'] as ProgramCurriculumVersionDto[] | undefined) ?? [],
        );
        setCourseEquivalences(
          (d['courseEquivalences'] as CourseEquivalenceDto[] | undefined) ?? [],
        );
        setCurrentCatalogYear((d['currentCatalogYear'] as string | undefined) ?? null);
        setEnforcePrerequisites((d['enforcePrerequisites'] as boolean | undefined) ?? false);
        setMinCoursesPerTerm((d['minCoursesPerTerm'] as number | undefined) ?? '');
        setOfferingMode(
          (d['offeringMode'] as 'open_catalog' | 'curated_cohort' | undefined) ?? 'curated_cohort',
        );
        const ct = d['capTarget'] as { capCode?: CapCode; level?: AcademicLevel } | undefined;
        setCapTargetCode(ct?.capCode ?? '');
        setCapTargetLevel(ct?.level ?? 'undergraduate');
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
    return unsub;
  }, [currentTenantId, programId]);

  // Load form templates
  useEffect(() => {
    if (!currentTenantId) return;
    getDocs(
      query(
        collection(db, `tenants/${currentTenantId}/formTemplates`),
        orderBy('createdAt', 'desc'),
      ),
    ).then((snap) => {
      setTemplates(
        snap.docs.map((d) => ({
          id: d.id,
          name: d.data()['name'] as string,
          fieldsCount: (d.data()['fields'] as any[])?.length ?? 0,
        })),
      );
    });
  }, [currentTenantId]);

  // Every program: the "builds on" choices, and the ladder this one is placed in.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      setAllPrograms(snap.docs.map((d) => toLadderProgram(d.id, d.data())));
    });
    return () => unsub();
  }, [currentTenantId]);

  // Load active departments for the Department selector
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/departments`), (snap) => {
      setDepartments(
        snap.docs
          .filter((d) => (((d.data() as any)['status'] as string) ?? 'active') === 'active')
          .map((d) => ({
            id: d.id,
            name: (d.data()['name'] as string) ?? d.id,
            code: (d.data()['code'] as string) ?? '',
          })),
      );
    });
    return () => unsub();
  }, [currentTenantId]);

  // Load the tenant's course catalog. Courses are program-agnostic (the malla IS
  // how a course is associated to a program), so we must NOT filter by the legacy
  // `programId` — that left the picker empty.
  //
  // INACTIVE COURSES ARE LOADED TOO, and flagged. A plan can legitimately list a
  // course that was later deactivated, and filtering it out here left the malla
  // unable to name it: the card printed a raw UUID and said nothing about why.
  // The PICKER still offers only active ones — see `curriculumCourseOptions`.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (snap) => {
      setCourses(
        snap.docs.map((d) => ({
          id: d.id,
          name: (d.data()['name'] as string) ?? d.id,
          code: (d.data()['code'] as string) ?? '',
          credits: (d.data()['credits'] as number) ?? 0,
          inactive: ((d.data() as any)['status'] as string | undefined) === 'inactive',
        })),
      );
      const prereqs: Record<string, string[]> = {};
      for (const d of snap.docs) {
        const p = (d.data()['prerequisites'] as string[] | undefined) ?? [];
        if (p.length) prereqs[d.id] = p;
      }
      setCoursePrereqs(prereqs);
    });
    return () => unsub();
  }, [currentTenantId]);

  // Course CCDs → per-course CAP area + TCU for the malla. One document per
  // course (`courseCcds/{courseId}`), so there is nothing to pick between: this
  // used to scan every TEMPLATE and keep "the first one that had a cap".
  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    void getDocs(collection(db, `tenants/${currentTenantId}/courseCcds`)).then((snap) => {
      if (!alive) return;
      const map: Record<string, { area?: SubjectAreaCode; tcu?: number }> = {};
      for (const d of snap.docs) {
        const card = (
          d.data() as { data?: { capSubjectArea?: SubjectAreaCode; tcu?: Record<string, number> } }
        ).data;
        if (!card) continue;
        const tcuVals = card.tcu ?? {};
        const tcu = tcuVals['undergraduate'] ?? tcuVals['graduate'] ?? Object.values(tcuVals)[0];
        map[d.id] = {
          ...(card.capSubjectArea ? { area: card.capSubjectArea } : {}),
          ...(tcu !== undefined ? { tcu } : {}),
        };
      }
      setCourseCap(map);
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editable || !currentTenantId || !programId) return;
    setError(null);
    setSuccess(false);
    // Required fields live in tab 0; with panels rendered as hidden the browser
    // can't focus an invalid one, so validate manually and jump to its tab.
    if (!name.trim() || !code.trim() || durationTerms === '') {
      setTab(0);
      setError(t('programDetail.validation.requiredGeneral'));
      return;
    }
    if (!noTuition && feeConfigured && !feeConfig.currency) {
      setTab(2);
      setError(t('programDetail.fees.currencyRequired'));
      return;
    }
    try {
      const cleanObjectives = learningObjectives
        .filter((o) => o.text.trim())
        .map((o) => ({
          id: o.id,
          ...(o.code && o.code.trim() ? { code: o.code.trim() } : {}),
          text: o.text.trim(),
        }));
      const cleanPhilosophy = philosophy
        .filter((p) => p.text.trim())
        .map((p) => ({
          id: p.id,
          title: (p.code ?? '').trim() || 'Compromiso',
          description: p.text.trim(),
        }));
      const cleanVersions: ProgramCurriculumVersionDto[] = curriculumVersions
        .filter((v) => v.catalogYear.trim())
        .map((v) => ({
          catalogYear: v.catalogYear.trim(),
          ...(v.status ? { status: v.status } : {}),
          // UNA SOLA REGLA, en `lib/curriculum-term.ts`. Acá se rearmaba el
          // término campo por campo, y `year`/`semester` se caían EN EL
          // NAVEGADOR: la pantalla decía que guardaba y el dato no salía.
          terms: v.terms.map(terminoParaGuardar),
        }));
      // A discontinued plan cannot be the live catalog: new enrollments would be
      // pinned to a plan the institution has stopped offering.
      const cleanCurrentCatalogYear =
        currentCatalogYear &&
        cleanVersions.some(
          (v) => v.catalogYear === currentCatalogYear && v.status !== 'discontinued',
        )
          ? currentCatalogYear
          : null;
      await updateProgram({
        tenantId: currentTenantId,
        programId,
        name,
        code,
        description,
        purpose,
        durationTerms: Number(durationTerms) || 0,
        status,
        formTemplateId: formTemplateId || null,
        documentRequirements,
        reviewerConfig,
        noTuition,
        ...(!noTuition && feeConfigured ? { feeConfig: feeConfig as ProgramFeeConfig } : {}),
        ...(primaryColor ? { primaryColor } : {}),
        minAttendancePct: minAttendancePct === '' ? null : Number(minAttendancePct),
        ...(departmentId ? { departmentId } : {}),
        learningObjectives: cleanObjectives,
        philosophy: cleanPhilosophy as any,
        rubrics: rubrics
          .filter((r) => r.title.trim() && r.criteria.length > 0)
          .map((r) => ({
            id: r.id,
            title: r.title.trim(),
            criteria: r.criteria.map((c) => ({
              id: c.id,
              label: c.label.trim() || 'Criterio',
              description: c.description.trim() || ' ',
              weight: Number.isFinite(c.weight) ? c.weight : 0,
              ...(c.levels && c.levels.length > 0
                ? {
                    levels: c.levels.map((l) => ({
                      id: l.id,
                      name: l.name.trim() || 'Nivel',
                      description: l.description.trim() || ' ',
                    })),
                  }
                : {}),
            })),
            ...(r.copiedFrom !== undefined ? { copiedFrom: r.copiedFrom } : {}),
          })),
        curriculumVersions: cleanVersions,
        // Half-written rows are dropped rather than rejected: the editor adds an
        // empty one on every click and the user may simply not have filled it.
        courseEquivalences: courseEquivalences
          .filter((e) => e.fromCourseId && e.toCourseId && e.fromCourseId !== e.toCourseId)
          .map((e) => ({
            fromCourseId: e.fromCourseId,
            toCourseId: e.toCourseId,
            ...(e.note?.trim() ? { note: e.note.trim() } : {}),
          })),
        currentCatalogYear: cleanCurrentCatalogYear,
        enforcePrerequisites,
        minCoursesPerTerm: minCoursesPerTerm === '' ? null : Number(minCoursesPerTerm),
        offeringMode,
        capTarget: capTargetCode ? { capCode: capTargetCode, level: capTargetLevel } : null,
      } as any);
      // Persist the attendance threshold via its dedicated callable — the
      // updateProgram gen2 deploy does not pick up the new schema field.
      await academicApi.setProgramMinAttendance({
        tenantId: currentTenantId,
        programId,
        minAttendancePct: minAttendancePct === '' ? null : Number(minAttendancePct),
      });
      // Without a program to build on, neither rule means anything.
      const ladderRules = {
        stacksOnProgramId,
        enforceStackEntry: stacksOnProgramId ? enforceStackEntry : false,
        includesStackedPrograms: stacksOnProgramId ? includesStackedPrograms : false,
      };
      if (
        ladderRules.stacksOnProgramId !== savedLadderRules.stacksOnProgramId ||
        ladderRules.enforceStackEntry !== savedLadderRules.enforceStackEntry ||
        ladderRules.includesStackedPrograms !== savedLadderRules.includesStackedPrograms
      ) {
        await academicApi.setProgramLadderRules({
          tenantId: currentTenantId,
          programId,
          ...ladderRules,
          stacksOnProgramId: ladderRules.stacksOnProgramId || null,
        });
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      // errorText, not err.message: a loop in the ladder comes back as a code the
      // browser translates, with the chain of program names in it.
      setError(errorText(err, t('programDetail.saveError')));
    }
  };

  // Course metadata for the visual malla (name/code + prerequisites + CAP area/TCU).
  const mallaCourseMeta = useMemo(() => {
    const m = new Map<string, MallaCourseMeta>();
    for (const c of courses) {
      const cap = courseCap[c.id];
      m.set(c.id, {
        id: c.id,
        name: c.name,
        code: c.code,
        prerequisites: coursePrereqs[c.id] ?? [],
        ...(c.inactive ? { inactive: true } : {}),
        ...(cap?.area ? { area: cap.area } : {}),
        ...(cap?.tcu !== undefined ? { tcu: cap.tcu } : {}),
      });
    }
    return m;
  }, [courses, coursePrereqs, courseCap]);

  // Options for the curriculum picker: the catalog plus the two numbers that
  // decide whether a plan is sound — credits (catalog) and TCU/area (the CCD).
  const curriculumCourseOptions = useMemo(
    () =>
      // Un curso inactivo no se puede seccionar, así que ofrecerlo para armar un
      // plan sería invitar a un hueco.
      courses
        .filter((c) => !c.inactive)
        .map((c) => {
          const cap = courseCap[c.id];
          return {
            ...c,
            ...(cap?.area ? { area: cap.area } : {}),
            ...(cap?.tcu !== undefined ? { tcu: cap.tcu } : {}),
          };
        }),
    [courses, courseCap],
  );

  // Where this program starts in its ladder, computed with the values on screen
  // (the edge and the plan being edited), so the hint answers before saving.
  const ladderPlacement = useMemo(() => {
    if (!programId) return null;
    const self: LadderProgram = {
      ...(allPrograms.find((p) => p.id === programId) ?? { id: programId, name: programName }),
      curriculumVersions,
      ...(currentCatalogYear ? { currentCatalogYear } : {}),
    };
    if (stacksOnProgramId) self.stacksOnProgramId = stacksOnProgramId;
    else delete self.stacksOnProgramId;
    const others = allPrograms.filter((p) => p.id !== programId);
    const ladder = buildProgramLadder([...others, self]);
    const row = ladder.rows.find((r) => r.programId === programId);
    if (!row) return null;
    return {
      start: row.startColumn + 1,
      end: row.startColumn + row.termCount,
      // Programs whose plans a cumulative award would add up, base first.
      chainNames: stackChain([...others, self], programId).map((p) => p.name),
    };
  }, [
    allPrograms,
    programId,
    programName,
    curriculumVersions,
    currentCatalogYear,
    stacksOnProgramId,
  ]);

  // The programs this one may build on: other ACTIVE programs. "Deleting" a
  // program in the app only makes it inactive, so inactive ones are junk to
  // offer — except the one already chosen, which must stay visible to be changed.
  const stackOptions = allPrograms
    .filter(
      (p) =>
        p.id !== programId && ((p.status ?? 'active') === 'active' || p.id === stacksOnProgramId),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedMallaVersion =
    curriculumVersions.find((v) => v.catalogYear === currentCatalogYear) ?? curriculumVersions[0];

  // Effective charge basis: legacy pricingMode fallback, and the old
  // 'per_program' name read as per_year (same rule as resolveChargeBasis).
  const feeBasis: 'per_year' | 'per_term' | 'per_course' | 'per_credit' =
    feeConfig.chargeBasis === 'per_program'
      ? 'per_year'
      : (feeConfig.chargeBasis ??
        (feeConfig.pricingMode === 'per-credit' ? 'per_credit' : 'per_term'));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={12}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
          mb: 4,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate('/academic')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {t('programDetail.backToPrograms')}
            </Button>
            <Typography color="text.disabled">/</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SchoolIcon sx={{ fontSize: 18, color: '#6366f1' }} />
              <Typography variant="h6" fontWeight={700}>
                {programName || t('programDetail.fallbackTitle')}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RocketLaunchRounded />}
              onClick={() => navigate(`/cap/${programId}/camino`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {tCap('journey.openFromProgram')}
            </Button>
            {editable && (
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveRounded />}
                disabled={isUpdatingProgram}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {isUpdatingProgram
                  ? t('programDetail.actions.saving')
                  : t('programDetail.actions.save')}
              </Button>
            )}
          </Stack>
        </Stack>

        {!editable && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('programDetail.readOnly')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('programDetail.updateSuccess')}
          </Alert>
        )}
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <Box px={3}>
        <Tabs
          value={tab}
          onChange={(_, v: number) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={t('programDetail.tabs.general')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label={t('programDetail.tabs.curriculum')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          {tabs.includes('finances') && (
            <Tab
              label={t('programDetail.tabs.finances')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          )}
          {/* S0·SEC-1b: documentos y comité de revisores son de gestión. El
              comité busca en el directorio completo de personas, que el
              docente y el mentor dejan de leer. Es la última pestaña: ocultarla
              no corre los índices de las otras. */}
          {tabs.includes('documents') && (
            <Tab
              label={t('programDetail.tabs.documents')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          )}
        </Tabs>
      </Box>

      {/* ── Content ────────────────────────────────────────────────── */}
      {/* Solo lectura: un fieldset deshabilitado bloquea los campos y botones
          nativos de adentro, sin tocar cada uno. */}
      <Box
        component="fieldset"
        disabled={!editable}
        sx={{ border: 0, m: 0, minWidth: 0 }}
        px={3}
        pb={6}
        pt={3}
      >
        <TabPanel value={tab} index={0}>
          {/* — Información general ─ */}
          <Section title={t('programDetail.sections.general')}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('programDetail.fields.name')}
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('programDetail.fields.code')}
                  required
                  fullWidth
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('programDetail.fields.durationTerms')}
                  type="number"
                  required
                  fullWidth
                  value={durationTerms}
                  onChange={(e) => setDurationTerms(e.target.value ? Number(e.target.value) : '')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('programDetail.fields.minAttendance', {
                    defaultValue: 'Asistencia mínima (%)',
                  })}
                  type="number"
                  fullWidth
                  value={minAttendancePct}
                  onChange={(e) =>
                    setMinAttendancePct(e.target.value ? Number(e.target.value) : '')
                  }
                  inputProps={{ min: 0, max: 100 }}
                  helperText={t('programDetail.fields.minAttendanceHelp', {
                    defaultValue: 'Vacío = 75% por defecto',
                  })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t('programDetail.fields.status')}
                  required
                  fullWidth
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                >
                  <MenuItem value="active">{t('programDetail.status.active')}</MenuItem>
                  <MenuItem value="inactive">{t('programDetail.status.inactive')}</MenuItem>
                  <MenuItem value="archived">{t('programDetail.status.archived')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t('programDetail.fields.formTemplate')}
                  fullWidth
                  value={formTemplateId}
                  onChange={(e) => setFormTemplateId(e.target.value)}
                  helperText={t('programDetail.fields.formTemplateHelp')}
                >
                  <MenuItem value="">
                    <em>{t('programDetail.fields.noFormTemplate')}</em>
                  </MenuItem>
                  {templates.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        {t('programDetail.fields.fieldsCount', { count: tpl.fieldsCount })}
                      </Typography>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('programDetail.fields.description')}
                  fullWidth
                  multiline
                  minRows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  helperText={t('programDetail.fields.descriptionHelp')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('programDetail.fields.purpose')}
                  fullWidth
                  multiline
                  minRows={4}
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  helperText={t('programDetail.fields.purposeHelp')}
                />
              </Grid>

              {/* — Certificación TMAI (CAP) ─ */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t('programDetail.capTarget.label')}
                  value={capTargetCode}
                  onChange={(e) => setCapTargetCode(e.target.value as '' | CapCode)}
                  helperText={t('programDetail.capTarget.help')}
                >
                  <MenuItem value="">{t('programDetail.capTarget.none')}</MenuItem>
                  <MenuItem value="BSC">{tCap('plos.caps.BSC')}</MenuItem>
                  <MenuItem value="DEP">{tCap('plos.caps.DEP')}</MenuItem>
                </TextField>
              </Grid>
              {capTargetCode && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label={t('programDetail.capTarget.level')}
                    value={capTargetLevel}
                    onChange={(e) => setCapTargetLevel(e.target.value as AcademicLevel)}
                  >
                    <MenuItem value="undergraduate">{tCap('levels.undergraduate')}</MenuItem>
                    <MenuItem value="graduate">{tCap('levels.graduate')}</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* — Color de acento ─ */}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('programDetail.accentColor.title')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('programDetail.accentColor.help')}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    {PROGRAM_COLOR_SWATCHES.map((hex) => (
                      <Tooltip key={hex} title={hex}>
                        <Box
                          role="button"
                          aria-label={t('programDetail.accentColor.swatchAria', { hex })}
                          onClick={() => setPrimaryColor(hex)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: hex,
                            cursor: 'pointer',
                            border:
                              primaryColor.toLowerCase() === hex.toLowerCase()
                                ? '3px solid #fff'
                                : '2px solid transparent',
                            boxShadow:
                              primaryColor.toLowerCase() === hex.toLowerCase()
                                ? `0 0 0 2px ${hex}`
                                : 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                            transition: 'transform 0.12s',
                            '&:hover': { transform: 'scale(1.1)' },
                          }}
                        />
                      </Tooltip>
                    ))}
                    <TextField
                      size="small"
                      placeholder="#7C3AED"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$', maxLength: 7 }}
                      sx={{ width: 130 }}
                      error={primaryColor !== '' && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)}
                      helperText={
                        primaryColor !== '' && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                          ? t('programDetail.accentColor.formatError')
                          : undefined
                      }
                    />
                    {primaryColor && (
                      <Button size="small" onClick={() => setPrimaryColor('')}>
                        {t('programDetail.accentColor.clear')}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Section>

          {/* — Departamento ─ */}
          <Section title={t('programDetail.sections.department')}>
            <Stack spacing={2}>
              <TextField
                select
                label={t('programDetail.department.label')}
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                helperText={t('programDetail.department.help')}
                fullWidth
              >
                <MenuItem value="">
                  <em>{t('programDetail.department.none')}</em>
                </MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.code ? `${d.code} — ` : ''}
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Section>

          {/* — OAPs ─ */}
          <Section title={t('programDetail.sections.objectives')}>
            <RepeatableListEditor
              label=""
              helperText={t('programDetail.objectives.help')}
              textLabel={t('programDetail.objectives.textLabel')}
              autoCodePrefix="OAP"
              items={learningObjectives}
              onChange={setLearningObjectives}
            />
          </Section>

          {/* — Filosofía ─ */}
          <Section title={t('programDetail.sections.philosophy')}>
            <RepeatableListEditor
              label=""
              helperText={t('programDetail.philosophy.help')}
              textLabel={t('programDetail.philosophy.textLabel')}
              autoCodePrefix=""
              showCode={true}
              items={philosophy}
              onChange={setPhilosophy}
            />
          </Section>

          {/* — Rúbricas ─ */}
          <Section title={t('programDetail.sections.rubrics')}>
            <RubricCatalogEditor
              value={rubrics}
              onChange={setRubrics}
              helperText={t('programDetail.rubrics.help')}
              {...(programId ? { currentProgramId: programId } : {})}
            />
          </Section>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {/* — Escalera de programas ─ */}
          <Section title={t('programDetail.stacksOn.title')}>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {t('programDetail.stacksOn.help')}
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
              >
                <TextField
                  select
                  size="small"
                  label={t('programDetail.stacksOn.label')}
                  value={stacksOnProgramId}
                  onChange={(e) => setStacksOnProgramId(e.target.value)}
                  sx={{ minWidth: 320 }}
                >
                  <MenuItem value="">{t('programDetail.stacksOn.none')}</MenuItem>
                  {stackOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  startIcon={<StairsRounded />}
                  onClick={() => navigate('/academic/programs/ladder')}
                >
                  {t('programDetail.stacksOn.viewLadder')}
                </Button>
              </Stack>
              {ladderPlacement && (
                <Typography variant="caption" color="text.secondary">
                  {ladderPlacement.start === ladderPlacement.end
                    ? t('programDetail.stacksOn.placementSingle', { n: ladderPlacement.start })
                    : t('programDetail.stacksOn.placement', {
                        start: ladderPlacement.start,
                        end: ladderPlacement.end,
                      })}
                </Typography>
              )}
              <Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!stacksOnProgramId && enforceStackEntry}
                      disabled={!stacksOnProgramId}
                      onChange={(e) => setEnforceStackEntry(e.target.checked)}
                    />
                  }
                  label={t('programDetail.stacksOn.enforceEntry')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
                  {t('programDetail.stacksOn.enforceEntryHelp')}
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={!!stacksOnProgramId && includesStackedPrograms}
                      disabled={!stacksOnProgramId}
                      onChange={(e) => setIncludesStackedPrograms(e.target.checked)}
                    />
                  }
                  label={t('programDetail.stacksOn.cumulative')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
                  {stacksOnProgramId && includesStackedPrograms && ladderPlacement
                    ? t('programDetail.stacksOn.cumulativeActive', {
                        programs: ladderPlacement.chainNames.join(', '),
                      })
                    : t('programDetail.stacksOn.cumulativeHelp')}
                </Typography>
              </Stack>
            </Stack>
          </Section>

          {/* — Mapa curricular (Phase D) ─ */}
          <Section title={t('program.curriculum.sectionTitle')}>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={curriculumView}
                onChange={(_, v: 'malla' | 'edit' | null) => v && setCurriculumView(v)}
              >
                <ToggleButton value="malla">{t('program.curriculum.viewMalla')}</ToggleButton>
                <ToggleButton value="edit">{t('program.curriculum.viewEdit')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {curriculumView === 'malla' ? (
              selectedMallaVersion ? (
                <MallaView terms={selectedMallaVersion.terms} courseMeta={mallaCourseMeta} />
              ) : (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  {t('program.curriculum.malla.empty')}
                </Typography>
              )
            ) : (
              <CurriculumMapEditor
                value={curriculumVersions}
                onChange={setCurriculumVersions}
                currentCatalogYear={currentCatalogYear}
                onCurrentCatalogYearChange={setCurrentCatalogYear}
                courses={curriculumCourseOptions}
              />
            )}
          </Section>

          {/* — Equivalencias entre catálogos ─ */}
          <Section title={t('program.equivalences.sectionTitle')}>
            <CourseEquivalenceEditor
              value={courseEquivalences}
              onChange={setCourseEquivalences}
              courses={curriculumCourseOptions}
            />
          </Section>

          {/* — Progresión y requisitos (Entrega 3A/3B + 2) ─ */}
          <Section title={t('programDetail.progression.title')}>
            <Stack spacing={2}>
              <TextField
                select
                label={t('programDetail.progression.offeringMode')}
                size="small"
                sx={{ maxWidth: 360 }}
                value={offeringMode}
                onChange={(e) =>
                  setOfferingMode(e.target.value as 'open_catalog' | 'curated_cohort')
                }
                helperText={t('programDetail.progression.offeringModeHelp')}
              >
                <MenuItem value="curated_cohort">
                  {t('programDetail.progression.curatedCohort')}
                </MenuItem>
                <MenuItem value="open_catalog">
                  {t('programDetail.progression.openCatalog')}
                </MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={enforcePrerequisites}
                    onChange={(e) => setEnforcePrerequisites(e.target.checked)}
                  />
                }
                label={t('programDetail.progression.enforcePrerequisites')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                {t('programDetail.progression.enforceHelp')}
              </Typography>
              <TextField
                label={t('programDetail.progression.minCoursesPerTerm')}
                type="number"
                size="small"
                sx={{ maxWidth: 260 }}
                value={minCoursesPerTerm}
                onChange={(e) =>
                  setMinCoursesPerTerm(e.target.value === '' ? '' : Number(e.target.value))
                }
                inputProps={{ min: 0 }}
                helperText={t('programDetail.progression.minCoursesHelp')}
              />
            </Stack>
          </Section>
        </TabPanel>

        {tabs.includes('finances') && (
          <TabPanel value={tab} index={2}>
            {/* — Aranceles ─ */}
            <Section title={t('programDetail.sections.fees')}>
              <FormControlLabel
                control={
                  <Switch checked={noTuition} onChange={(e) => setNoTuition(e.target.checked)} />
                }
                label={t('programDetail.fees.noTuition')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('programDetail.fees.noTuitionHelp')}
              </Typography>
              {!noTuition && !feeConfigured && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('programDetail.fees.notConfigured')}
                </Alert>
              )}
              {!noTuition && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t('programDetail.fees.enrollmentFee')}
                      type="number"
                      fullWidth
                      value={feeConfig.enrollmentFee}
                      onChange={(e) =>
                        updateFee((p) => ({ ...p, enrollmentFee: Number(e.target.value) || 0 }))
                      }
                      inputProps={{ min: 0 }}
                      helperText={
                        feeBasis === 'per_year'
                          ? t('programDetail.fees.enrollmentFeeHelpPerYear')
                          : t('programDetail.fees.enrollmentFeeHelpPerTerm')
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label={t('programDetail.fees.chargeBasis')}
                      fullWidth
                      value={feeBasis}
                      onChange={(e) => {
                        const cb = e.target.value as typeof feeBasis;
                        updateFee((p) => ({
                          ...p,
                          chargeBasis: cb,
                          // Keep the legacy pricingMode in sync for back-compat readers.
                          pricingMode: cb === 'per_credit' ? 'per-credit' : 'fixed',
                        }));
                      }}
                      helperText={t('programDetail.fees.chargeBasisHelp')}
                    >
                      <MenuItem value="per_year">{t('programDetail.fees.basisPerYear')}</MenuItem>
                      <MenuItem value="per_term">{t('programDetail.fees.basisPerTerm')}</MenuItem>
                      <MenuItem value="per_course">
                        {t('programDetail.fees.basisPerCourse')}
                      </MenuItem>
                      <MenuItem value="per_credit">
                        {t('programDetail.fees.basisPerCredit')}
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label={t('programDetail.fees.currency')}
                      fullWidth
                      required
                      value={feeConfig.currency}
                      onChange={(e) =>
                        updateFee((p) => ({ ...p, currency: e.target.value as any }))
                      }
                    >
                      <MenuItem value="" disabled>
                        {t('programDetail.fees.currencyChoose')}
                      </MenuItem>
                      <MenuItem value="CLP">{t('programDetail.fees.currencyCLP')}</MenuItem>
                      <MenuItem value="ARS">{t('programDetail.fees.currencyARS')}</MenuItem>
                      <MenuItem value="BRL">{t('programDetail.fees.currencyBRL')}</MenuItem>
                      <MenuItem value="COP">{t('programDetail.fees.currencyCOP')}</MenuItem>
                      <MenuItem value="MXN">{t('programDetail.fees.currencyMXN')}</MenuItem>
                      <MenuItem value="PEN">{t('programDetail.fees.currencyPEN')}</MenuItem>
                      <MenuItem value="UYU">{t('programDetail.fees.currencyUYU')}</MenuItem>
                      <MenuItem value="USD">{t('programDetail.fees.currencyUSD')}</MenuItem>
                      <MenuItem value="EUR">{t('programDetail.fees.currencyEUR')}</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Campo de tarifa condicional según el charge basis */}
                  {(() => {
                    const basis = feeBasis;
                    if (basis === 'per_credit') {
                      return (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('programDetail.fees.pricePerCredit')}
                            type="number"
                            fullWidth
                            value={feeConfig.pricePerCredit ?? ''}
                            onChange={(e) =>
                              updateFee((p) => ({
                                ...p,
                                pricePerCredit: e.target.value ? Number(e.target.value) : 0,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            helperText={t('programDetail.fees.pricePerCreditHelp')}
                          />
                        </Grid>
                      );
                    }
                    if (basis === 'per_course') {
                      return (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('programDetail.fees.ratePerCourse')}
                            type="number"
                            fullWidth
                            value={feeConfig.ratePerCourse ?? ''}
                            onChange={(e) =>
                              updateFee((p) => ({
                                ...p,
                                ratePerCourse: e.target.value ? Number(e.target.value) : 0,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            helperText={t('programDetail.fees.ratePerCourseHelp')}
                          />
                        </Grid>
                      );
                    }
                    // Flat bases: the same amount field, named by how often it is charged.
                    const perYear = basis === 'per_year';
                    return (
                      <>
                        <Grid item xs={12} md={perYear ? 4 : 6}>
                          <TextField
                            label={
                              perYear
                                ? t('programDetail.fees.feeAmountPerYear')
                                : t('programDetail.fees.feeAmountPerTerm')
                            }
                            type="number"
                            fullWidth
                            value={feeConfig.programTotalFee}
                            onChange={(e) =>
                              updateFee((p) => ({
                                ...p,
                                programTotalFee: Number(e.target.value) || 0,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            helperText={
                              perYear
                                ? t('programDetail.fees.feeAmountPerYearHelp')
                                : t('programDetail.fees.feeAmountPerTermHelp')
                            }
                          />
                        </Grid>
                        {perYear && (
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              label={t('programDetail.fees.annualYearBasis')}
                              fullWidth
                              value={feeConfig.annualYearBasis ?? 'calendar'}
                              onChange={(e) =>
                                updateFee((p) => ({
                                  ...p,
                                  annualYearBasis: e.target.value as 'calendar' | 'program',
                                }))
                              }
                              helperText={t('programDetail.fees.annualYearBasisHelp')}
                            >
                              <MenuItem value="calendar">
                                {t('programDetail.fees.annualYearBasisCalendar')}
                              </MenuItem>
                              <MenuItem value="program">
                                {t('programDetail.fees.annualYearBasisProgram')}
                              </MenuItem>
                            </TextField>
                          </Grid>
                        )}
                      </>
                    );
                  })()}
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label={t('programDetail.fees.paymentMode')}
                      fullWidth
                      value={feeConfig.paymentMode ?? 'immediate'}
                      onChange={(e) =>
                        updateFee((p) => ({
                          ...p,
                          paymentMode: e.target.value as 'immediate' | 'pay_to_enroll',
                        }))
                      }
                      helperText={t('programDetail.fees.paymentModeHelp')}
                    >
                      <MenuItem value="immediate">
                        {t('programDetail.fees.paymentModeImmediate')}
                      </MenuItem>
                      <MenuItem value="pay_to_enroll">
                        {t('programDetail.fees.paymentModePayToEnroll')}
                      </MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              )}
            </Section>
          </TabPanel>
        )}

        {tabs.includes('documents') && (
          <TabPanel value={tab} index={3}>
            {/* — Documentos requeridos ─ */}
            <Section title={t('programDetail.sections.documents')}>
              <ManageDocumentRequirements
                requirements={documentRequirements}
                onChange={setDocumentRequirements}
              />
            </Section>

            {/* — Comité de revisores ─ */}
            <Section title={t('programDetail.sections.reviewCommittee')}>
              <ManageReviewCommittee reviewerConfig={reviewerConfig} onChange={setReviewerConfig} />
            </Section>
          </TabPanel>
        )}
      </Box>
    </form>
  );
}
